// src/pages/USAExchangeRatePage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../config/api";

type USASaleRecord = {
  id: number;
  bill_no: string;
  order_item_id: number;
  qty: number;
  usd: number;
  thb: number;
  exchange_rate: number;
  note: string | null;
  sold_at: string | null;
};

type USAOrderItem = {
  id: number;
  product_code: string | null;
  product_name: string | null;
};

type USAOrder = {
  bill_no: string;
  usa_status: string;
  usa_payout_usd: number;
  usa_payout_thb: number;
  usa_exchange_rate: number;
  created_at: string | null;
  order_items: USAOrderItem[];
  sale_records: USASaleRecord[];
};

type USADeletedArchive = {
  id: number;
  bill_no: string;
  order_usa_status: string | null;
  order_usa_exchange_rate: number;
  record_count: number;
  total_qty: number;
  total_usd: number;
  total_thb: number;
  reason: string | null;
  deleted_by_username: string | null;
  deleted_at: string | null;
  restored_by_username: string | null;
  restored_at: string | null;
  status: "DELETED" | "RESTORED" | string;
  items: USADeletedArchiveItem[];
};

type USADeletedArchiveItem = {
  original_record_id: number;
  bill_no: string;
  order_item_id: number;
  product_code: string | null;
  product_name: string | null;
  qty: number;
  usd: number;
  thb: number;
  exchange_rate: number;
  note: string | null;
  sold_at: string | null;
};

type USAOrdersResponse = { ok?: boolean; items?: unknown; error?: string };
type ArchiveResponse = { ok?: boolean; items?: unknown; error?: string };
type ActiveTab = "MISSING" | "APPLIED" | "DELETED";

const TOKEN_STORAGE_KEY = "bg_shop_token";

function toNumber(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toUSASaleRecord(row: unknown): USASaleRecord | null {
  if (!row || typeof row !== "object") return null;
  const d = row as Record<string, unknown>;
  const id = Number(d.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  return {
    id,
    bill_no: typeof d.bill_no === "string" ? d.bill_no : "",
    order_item_id: Number(d.order_item_id),
    qty: toNumber(d.qty),
    usd: toNumber(d.usd),
    thb: toNumber(d.thb),
    exchange_rate: toNumber(d.exchange_rate),
    note: typeof d.note === "string" ? d.note : null,
    sold_at: typeof d.sold_at === "string" ? d.sold_at : null,
  };
}

function toUSAOrderItem(row: unknown): USAOrderItem | null {
  if (!row || typeof row !== "object") return null;
  const d = row as Record<string, unknown>;
  const id = Number(d.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  return {
    id,
    product_code: typeof d.product_code === "string" ? d.product_code : null,
    product_name: typeof d.product_name === "string" ? d.product_name : null,
  };
}

function toUSAOrder(row: unknown): USAOrder | null {
  if (!row || typeof row !== "object") return null;
  const d = row as Record<string, unknown>;
  const bill_no = typeof d.bill_no === "string" ? d.bill_no.trim() : "";
  if (!bill_no) return null;
  return {
    bill_no,
    usa_status:
      typeof d.usa_status === "string" && d.usa_status.trim()
        ? d.usa_status.trim().toUpperCase()
        : "PENDING",
    usa_payout_usd: toNumber(d.usa_payout_usd),
    usa_payout_thb: toNumber(d.usa_payout_thb),
    usa_exchange_rate: toNumber(d.usa_exchange_rate),
    created_at: typeof d.created_at === "string" ? d.created_at : null,
    order_items: Array.isArray(d.order_items)
      ? d.order_items.map(toUSAOrderItem).filter((x): x is USAOrderItem => x != null)
      : [],
    sale_records: Array.isArray(d.sale_records)
      ? d.sale_records.map(toUSASaleRecord).filter((x): x is USASaleRecord => x != null)
      : [],
  };
}

function toArchive(row: unknown): USADeletedArchive | null {
  if (!row || typeof row !== "object") return null;
  const d = row as Record<string, unknown>;
  const id = Number(d.id);
  const bill_no = typeof d.bill_no === "string" ? d.bill_no.trim() : "";
  if (!Number.isInteger(id) || id <= 0 || !bill_no) return null;
  return {
    id,
    bill_no,
    order_usa_status: typeof d.order_usa_status === "string" ? d.order_usa_status : null,
    order_usa_exchange_rate: toNumber(d.order_usa_exchange_rate),
    record_count: toNumber(d.record_count),
    total_qty: toNumber(d.total_qty),
    total_usd: toNumber(d.total_usd),
    total_thb: toNumber(d.total_thb),
    reason: typeof d.reason === "string" && d.reason.trim() ? d.reason : null,
    deleted_by_username: typeof d.deleted_by_username === "string" ? d.deleted_by_username : null,
    deleted_at: typeof d.deleted_at === "string" ? d.deleted_at : null,
    restored_by_username: typeof d.restored_by_username === "string" ? d.restored_by_username : null,
    restored_at: typeof d.restored_at === "string" ? d.restored_at : null,
    status: typeof d.status === "string" ? d.status : "DELETED",
    items: Array.isArray(d.items) ? d.items.map(toArchiveItem).filter((x): x is USADeletedArchiveItem => x != null) : [],
  };
}

function toArchiveItem(row: unknown): USADeletedArchiveItem | null {
  if (!row || typeof row !== "object") return null;
  const d = row as Record<string, unknown>;
  const original_record_id = Number(d.original_record_id);
  if (!Number.isInteger(original_record_id) || original_record_id <= 0) return null;
  return {
    original_record_id,
    bill_no: typeof d.bill_no === "string" ? d.bill_no : "",
    order_item_id: Number(d.order_item_id),
    product_code: typeof d.product_code === "string" ? d.product_code : null,
    product_name: typeof d.product_name === "string" ? d.product_name : null,
    qty: toNumber(d.qty),
    usd: toNumber(d.usd),
    thb: toNumber(d.thb),
    exchange_rate: toNumber(d.exchange_rate),
    note: typeof d.note === "string" ? d.note : null,
    sold_at: typeof d.sold_at === "string" ? d.sold_at : null,
  };
}

function formatCurrency(value: number, currency: "THB" | "USD" = "THB"): string {
  const locale = currency === "USD" ? "en-US" : "th-TH";
  return value.toLocaleString(locale, { style: "currency", currency });
}

function formatDateTime(value: string | null): string {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("th-TH", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function isMissingRateBill(order: USAOrder): boolean {
  if (order.sale_records.length === 0) return false;
  return (
    order.usa_exchange_rate <= 0 ||
    order.sale_records.some((rec) => rec.exchange_rate <= 0 || (rec.usd > 0 && rec.thb <= 0))
  );
}

function sumRecords(order: USAOrder): { records: number; qty: number; usd: number; thb: number } {
  return order.sale_records.reduce(
    (sum, rec) => ({
      records: sum.records + 1,
      qty: sum.qty + rec.qty,
      usd: sum.usd + rec.usd,
      thb: sum.thb + rec.thb,
    }),
    { records: 0, qty: 0, usd: 0, thb: 0 }
  );
}

function sumOrders(orders: USAOrder[]): { bills: number; records: number; qty: number; usd: number; thb: number } {
  return orders.reduce(
    (sum, order) => {
      const row = sumRecords(order);
      return {
        bills: sum.bills + 1,
        records: sum.records + row.records,
        qty: sum.qty + row.qty,
        usd: sum.usd + row.usd,
        thb: sum.thb + row.thb,
      };
    },
    { bills: 0, records: 0, qty: 0, usd: 0, thb: 0 }
  );
}

function sumArchives(archives: USADeletedArchive[]) {
  return archives.reduce(
    (sum, row) => ({
      bills: sum.bills + 1,
      records: sum.records + row.record_count,
      qty: sum.qty + row.total_qty,
      usd: sum.usd + row.total_usd,
      thb: sum.thb + row.total_thb,
    }),
    { bills: 0, records: 0, qty: 0, usd: 0, thb: 0 }
  );
}

function errorMessageFromCode(code: string): string {
  if (code === "rate_applied_cannot_archive") return "บิลนี้ใส่เรทแล้ว จึงลบยอดขายจากหน้านี้ไม่ได้";
  if (code === "no_sale_records_to_archive") return "บิลนี้ไม่มี records ให้ลบ";
  if (code === "archive_already_restored") return "รายการนี้ถูกกู้คืนไปแล้ว";
  if (code === "restore_qty_exceeds_remaining") return "กู้คืนไม่ได้ เพราะสินค้านี้ถูกขายใหม่ไปแล้วจน remaining ไม่พอ";
  if (code === "restore_order_item_missing") return "กู้คืนไม่ได้ เพราะ order item เดิมไม่อยู่แล้ว";
  if (code === "order_not_found") return "ไม่พบบิลเดิมในระบบ";
  return code || "ทำรายการไม่สำเร็จ";
}

export default function USAExchangeRatePage(): JSX.Element {
  const [orders, setOrders] = useState<USAOrder[]>([]);
  const [archives, setArchives] = useState<USADeletedArchive[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingArchives, setLoadingArchives] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [activeTab, setActiveTab] = useState<ActiveTab>("MISSING");
  const [rateInput, setRateInput] = useState("");
  const [applying, setApplying] = useState(false);
  const [archivingBillNo, setArchivingBillNo] = useState<string | null>(null);
  const [restoringArchiveId, setRestoringArchiveId] = useState<number | null>(null);
  const [expandedBillNos, setExpandedBillNos] = useState<Record<string, boolean>>({});
  const [expandedArchiveIds, setExpandedArchiveIds] = useState<Record<number, boolean>>({});

  async function loadOrders(): Promise<void> {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const resp = await fetch(apiUrl("/usa-shop-orders"), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = (await resp.json().catch(() => ({}))) as USAOrdersResponse;
      if (!resp.ok || !data.ok || !Array.isArray(data.items)) {
        throw new Error(data.error ?? "failed_to_fetch_usa_shop_orders");
      }
      setOrders(data.items.map(toUSAOrder).filter((x): x is USAOrder => x != null));
    } catch (err) {
      setError(err instanceof Error ? err.message : "โหลดบิล USA ไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  }

  async function loadArchives(): Promise<void> {
    setLoadingArchives(true);
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const resp = await fetch(apiUrl("/orders/usa-sale-records/archives"), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = (await resp.json().catch(() => ({}))) as ArchiveResponse;
      if (!resp.ok || !data.ok || !Array.isArray(data.items)) {
        throw new Error(data.error ?? "failed_to_fetch_archives");
      }
      setArchives(data.items.map(toArchive).filter((x): x is USADeletedArchive => x != null));
    } catch (err) {
      setError(err instanceof Error ? err.message : "โหลดประวัติการลบไม่สำเร็จ");
    } finally {
      setLoadingArchives(false);
    }
  }

  async function loadAll(): Promise<void> {
    await Promise.all([loadOrders(), loadArchives()]);
  }

  useEffect(() => {
    void loadAll();
  }, []);

  // PMN-CRITICAL(/USAExchangeRatePage): missing/applied split must match backend apply-exchange-rate scope.
  const { missingOrders, appliedOrders } = useMemo(() => {
    const withRecords = orders.filter((order) => order.sale_records.length > 0);
    const missing = withRecords.filter(isMissingRateBill);
    const applied = withRecords.filter((order) => !isMissingRateBill(order));
    return { missingOrders: missing, appliedOrders: applied };
  }, [orders]);

  const missingSummary = useMemo(() => sumOrders(missingOrders), [missingOrders]);
  const appliedSummary = useMemo(() => sumOrders(appliedOrders), [appliedOrders]);
  const archiveSummary = useMemo(() => sumArchives(archives), [archives]);
  const visibleOrders = activeTab === "MISSING" ? missingOrders : activeTab === "APPLIED" ? appliedOrders : [];
  const visibleSummary =
    activeTab === "MISSING" ? missingSummary : activeTab === "APPLIED" ? appliedSummary : archiveSummary;
  const isLoadingCurrent = activeTab === "DELETED" ? loadingArchives : loading;

  async function applyExchangeRate(): Promise<void> {
    const rate = Number(rateInput);
    if (!Number.isFinite(rate) || rate <= 0) {
      setError("rate ต้องเป็นตัวเลขมากกว่า 0");
      return;
    }
    if (missingOrders.length === 0) {
      setError("ไม่มีบิลที่ต้องใส่เรท");
      return;
    }

    const ok = window.confirm(
      `ใส่ rate ${rate} ให้ ${missingSummary.bills} บิล / ${missingSummary.records} records?\n` +
        `USD รวม ${missingSummary.usd.toFixed(2)} จะถูกคำนวณ THB ใหม่ทั้งหมดด้วย rate นี้`
    );
    if (!ok) return;

    setApplying(true);
    setError("");
    setSuccessMessage("");
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const resp = await fetch(apiUrl("/orders/usa-sale-records/apply-exchange-rate"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ exchange_rate: rate }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) {
        throw new Error(errorMessageFromCode(typeof data?.error === "string" ? data.error : "failed_to_apply_exchange_rate"));
      }
      setSuccessMessage(`ใส่เรทเรียบร้อย ${data.updated_bills ?? 0} บิล / ${data.updated_records ?? 0} records`);
      setRateInput("");
      await loadAll();
      setActiveTab("APPLIED");
    } catch (err) {
      setError(err instanceof Error ? err.message : "ใส่เรทไม่สำเร็จ");
    } finally {
      setApplying(false);
    }
  }

  async function archiveOrder(order: USAOrder): Promise<void> {
    if (!isMissingRateBill(order)) {
      setError("บิลนี้ใส่เรทแล้ว จึงลบยอดขายจากหน้านี้ไม่ได้");
      return;
    }
    const sums = sumRecords(order);
    const reason = window.prompt(
      `ลบยอดขายของบิล #${order.bill_no}?\n` +
        `Records ${sums.records} / Qty ${sums.qty} / USD ${sums.usd.toFixed(2)}\n\n` +
        `ระบบจะพัก records และคืน USA remaining ให้ขายใหม่ได้\n` +
        `ใส่เหตุผลการลบ (เว้นว่างได้):`,
      ""
    );
    if (reason === null) return;

    setArchivingBillNo(order.bill_no);
    setError("");
    setSuccessMessage("");
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const resp = await fetch(apiUrl(`/orders/${encodeURIComponent(order.bill_no)}/usa-sale-records/archive`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ reason }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) {
        throw new Error(errorMessageFromCode(typeof data?.error === "string" ? data.error : "failed_to_archive"));
      }
      setSuccessMessage(`ลบยอดขาย #${order.bill_no} แล้ว เก็บประวัติไว้ที่ archive #${data.archive_id}`);
      await loadAll();
      setActiveTab("DELETED");
    } catch (err) {
      setError(err instanceof Error ? err.message : "ลบยอดขายไม่สำเร็จ");
    } finally {
      setArchivingBillNo(null);
    }
  }

  async function restoreArchive(archive: USADeletedArchive): Promise<void> {
    if (archive.status !== "DELETED") return;
    const ok = window.confirm(
      `กู้คืนยอดขายของบิล #${archive.bill_no}?\n` +
        `Records ${archive.record_count} / Qty ${archive.total_qty} / USD ${archive.total_usd.toFixed(2)}\n\n` +
        `ระบบจะเช็ค remaining อีกครั้ง ถ้าไม่พอจะไม่กู้คืนบางส่วน`
    );
    if (!ok) return;

    setRestoringArchiveId(archive.id);
    setError("");
    setSuccessMessage("");
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const resp = await fetch(apiUrl(`/orders/usa-sale-records/archives/${archive.id}/restore`), {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) {
        throw new Error(errorMessageFromCode(typeof data?.error === "string" ? data.error : "failed_to_restore"));
      }
      setSuccessMessage(`กู้คืนยอดขาย #${archive.bill_no} แล้ว ${data.restored_records ?? archive.record_count} records`);
      await loadAll();
      setActiveTab("MISSING");
    } catch (err) {
      setError(err instanceof Error ? err.message : "กู้คืนไม่สำเร็จ");
    } finally {
      setRestoringArchiveId(null);
    }
  }

  function toggleBillDetails(billNo: string): void {
    setExpandedBillNos((prev) => ({ ...prev, [billNo]: !prev[billNo] }));
  }

  function toggleArchiveDetails(id: number): void {
    setExpandedArchiveIds((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <div className="min-vh-100 bg-light">
      <div className="container py-4 py-md-5">
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
          <div>
            <h4 className="mb-1 text-danger">🇺🇸 ใส่เรทขาย USA</h4>
            <div className="small text-muted">คำนวณ THB ย้อนหลังให้ records ที่มาจาก USD-only</div>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary btn-sm" disabled={loading || loadingArchives} onClick={() => void loadAll()}>
              รีเฟรช
            </button>
            <a href="/USASalesPayoutPage" className="btn btn-outline-primary btn-sm">
              บันทึกยอดขาย USA
            </a>
            <a href="/" className="btn btn-link text-decoration-none">
              กลับเมนู
            </a>
          </div>
        </div>

        {error && <div className="alert alert-warning small">{error}</div>}
        {successMessage && <div className="alert alert-success small">{successMessage}</div>}

        <div className="card border-0 shadow-sm rounded-4 mb-3">
          <div className="card-body p-3">
            <div className="d-flex flex-wrap gap-2 align-items-end justify-content-between">
              <div className="btn-group flex-wrap" role="group">
                <button
                  type="button"
                  className={`btn ${activeTab === "MISSING" ? "btn-danger" : "btn-outline-danger"}`}
                  onClick={() => setActiveTab("MISSING")}
                >
                  ยังไม่ได้ใส่เรท ({missingSummary.bills})
                </button>
                <button
                  type="button"
                  className={`btn ${activeTab === "APPLIED" ? "btn-success" : "btn-outline-success"}`}
                  onClick={() => setActiveTab("APPLIED")}
                >
                  ใส่เรทแล้ว ({appliedSummary.bills})
                </button>
                <button
                  type="button"
                  className={`btn ${activeTab === "DELETED" ? "btn-secondary" : "btn-outline-secondary"}`}
                  onClick={() => setActiveTab("DELETED")}
                >
                  ลบแล้ว ({archiveSummary.bills})
                </button>
              </div>

              {activeTab === "MISSING" && (
                <div className="d-flex flex-wrap gap-2 align-items-end">
                  <div>
                    <label className="form-label small text-muted mb-1">Rate กลาง (1 USD = ? THB)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="form-control"
                      style={{ width: 180 }}
                      placeholder="เช่น 35.5000"
                      value={rateInput}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw !== "" && !/^\d*\.?\d{0,4}$/.test(raw)) return;
                        setRateInput(raw);
                      }}
                    />
                  </div>
                  <button
                    className="btn btn-danger"
                    disabled={applying || missingOrders.length === 0 || rateInput.trim() === ""}
                    onClick={() => void applyExchangeRate()}
                  >
                    {applying ? "กำลังใส่เรท..." : "ใส่ให้ทุกบิล"}
                  </button>
                </div>
              )}
            </div>

            <div className="row g-2 mt-3">
              <div className="col-6 col-md-3">
                <div className="small text-muted">Bills</div>
                <div className="fs-5 fw-semibold">{visibleSummary.bills}</div>
              </div>
              <div className="col-6 col-md-3">
                <div className="small text-muted">Records</div>
                <div className="fs-5 fw-semibold">{visibleSummary.records}</div>
              </div>
              <div className="col-6 col-md-3">
                <div className="small text-muted">USD รวม</div>
                <div className="fs-5 fw-semibold">{formatCurrency(visibleSummary.usd, "USD")}</div>
              </div>
              <div className="col-6 col-md-3">
                <div className="small text-muted">THB รวม</div>
                <div className="fs-5 fw-semibold">{formatCurrency(visibleSummary.thb)}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body p-0">
            {isLoadingCurrent ? (
              <div className="text-center text-muted py-5">กำลังโหลด...</div>
            ) : activeTab === "DELETED" ? (
              archives.length === 0 ? (
                <div className="text-center text-muted py-5">ยังไม่มีประวัติการลบ</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Bill No</th>
                        <th>Deleted</th>
                        <th>Reason</th>
                        <th className="text-end">Records</th>
                        <th className="text-end">USD</th>
                        <th className="text-end">THB</th>
                        <th>Status</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {archives.map((archive) => {
                        const isExpanded = expandedArchiveIds[archive.id] === true;
                        return (
                          <React.Fragment key={archive.id}>
                            <tr className={isExpanded ? "table-primary" : undefined}>
                              <td className="fw-semibold">#{archive.bill_no}</td>
                              <td>
                                <div>{formatDateTime(archive.deleted_at)}</div>
                                <div className="small text-muted">{archive.deleted_by_username ?? "-"}</div>
                              </td>
                              <td className="small">{archive.reason ?? "-"}</td>
                              <td className="text-end">{archive.record_count}</td>
                              <td className="text-end">{formatCurrency(archive.total_usd, "USD")}</td>
                              <td className="text-end">{formatCurrency(archive.total_thb)}</td>
                              <td>
                                <span className={`badge ${archive.status === "DELETED" ? "text-bg-secondary" : "text-bg-success"}`}>
                                  {archive.status === "DELETED" ? "ลบแล้ว" : "กู้คืนแล้ว"}
                                </span>
                                {archive.status !== "DELETED" && (
                                  <div className="small text-muted mt-1">
                                    {formatDateTime(archive.restored_at)} {archive.restored_by_username ?? ""}
                                  </div>
                                )}
                              </td>
                              <td className="text-end">
                                <div className="d-flex justify-content-end gap-2">
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => toggleArchiveDetails(archive.id)}
                                  >
                                    {isExpanded ? "ซ่อนสินค้า" : "ดูสินค้า"}
                                  </button>
                                  {archive.status === "DELETED" ? (
                                    <button
                                      className="btn btn-sm btn-outline-success"
                                      disabled={restoringArchiveId === archive.id}
                                      onClick={() => void restoreArchive(archive)}
                                    >
                                      {restoringArchiveId === archive.id ? "กำลังกู้คืน..." : "กู้คืน"}
                                    </button>
                                  ) : (
                                    <button className="btn btn-sm btn-outline-secondary" disabled>
                                      กู้คืนแล้ว
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={8} className="bg-light">
                                  <div className="p-3">
                                    <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                                      <div className="fw-semibold">รายการที่เคยขายในบิล #{archive.bill_no}</div>
                                      <div className="small text-muted">
                                        รวม {archive.total_qty} ชิ้น / {formatCurrency(archive.total_usd, "USD")} / {formatCurrency(archive.total_thb)}
                                      </div>
                                    </div>
                                    {archive.items.length === 0 ? (
                                      <div className="small text-muted">ไม่มีรายละเอียดสินค้าใน archive นี้</div>
                                    ) : (
                                      <div className="table-responsive">
                                        <table className="table table-sm align-middle mb-0">
                                          <thead>
                                            <tr>
                                              <th>สินค้า</th>
                                              <th className="text-end">Qty</th>
                                              <th className="text-end">USD</th>
                                              <th className="text-end">Rate</th>
                                              <th className="text-end">THB</th>
                                              <th>Sold At</th>
                                              <th>Note</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {archive.items.map((item) => (
                                              <tr key={item.original_record_id}>
                                                <td>
                                                  <div className="fw-semibold">{item.product_code ?? `item #${item.order_item_id}`}</div>
                                                  <div className="small text-muted">{item.product_name ?? "ไม่พบชื่อสินค้า"}</div>
                                                </td>
                                                <td className="text-end">{item.qty}</td>
                                                <td className="text-end">{formatCurrency(item.usd, "USD")}</td>
                                                <td className={`text-end ${item.exchange_rate > 0 ? "" : "text-danger fw-semibold"}`}>
                                                  {item.exchange_rate > 0 ? item.exchange_rate.toFixed(4) : "ยังไม่มี"}
                                                </td>
                                                <td className="text-end">{formatCurrency(item.thb)}</td>
                                                <td>{formatDateTime(item.sold_at)}</td>
                                                <td className="small text-muted">{item.note ?? "-"}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            ) : visibleOrders.length === 0 ? (
              <div className="text-center text-muted py-5">
                {activeTab === "MISSING" ? "ไม่มีบิลที่ต้องใส่เรท" : "ยังไม่มีบิลที่ใส่เรทครบ"}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Bill No</th>
                      <th>Created</th>
                      <th>Status</th>
                      <th className="text-end">Records</th>
                      <th className="text-end">Rate บิล</th>
                      <th className="text-end">USD</th>
                      <th className="text-end">THB</th>
                      <th className="text-end">Records ไม่มีเรท</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleOrders.map((order) => {
                      const sums = sumRecords(order);
                      const missingRecordCount = order.sale_records.filter(
                        (rec) => rec.exchange_rate <= 0 || (rec.usd > 0 && rec.thb <= 0)
                      ).length;
                      const canArchive = activeTab === "MISSING" && isMissingRateBill(order);
                      const isExpanded = expandedBillNos[order.bill_no] === true;
                      const itemById = new Map(order.order_items.map((item) => [item.id, item]));
                      return (
                        <React.Fragment key={order.bill_no}>
                          <tr className={isExpanded ? "table-primary" : undefined}>
                            <td className="fw-semibold">#{order.bill_no}</td>
                            <td>{formatDateTime(order.created_at)}</td>
                            <td>
                              <span className={`badge ${order.usa_status === "COMPLETED" ? "text-bg-success" : "text-bg-danger"}`}>
                                {order.usa_status === "COMPLETED" ? "เสร็จสิ้น" : "ยังไม่ครบ"}
                              </span>
                            </td>
                            <td className="text-end">{sums.records}</td>
                            <td className={`text-end ${order.usa_exchange_rate > 0 ? "" : "text-danger fw-semibold"}`}>
                              {order.usa_exchange_rate > 0 ? order.usa_exchange_rate.toFixed(4) : "ยังไม่มี"}
                            </td>
                            <td className="text-end">{formatCurrency(sums.usd, "USD")}</td>
                            <td className="text-end">{formatCurrency(sums.thb)}</td>
                            <td className="text-end">
                              {missingRecordCount > 0 ? (
                                <span className="badge text-bg-warning">{missingRecordCount}</span>
                              ) : (
                                <span className="text-muted">0</span>
                              )}
                            </td>
                            <td className="text-end">
                              <div className="d-flex justify-content-end gap-2">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => toggleBillDetails(order.bill_no)}
                                >
                                  {isExpanded ? "ซ่อนสินค้า" : "ดูสินค้า"}
                                </button>
                                {canArchive ? (
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    disabled={archivingBillNo === order.bill_no}
                                    onClick={() => void archiveOrder(order)}
                                  >
                                    {archivingBillNo === order.bill_no ? "กำลังลบ..." : "ลบยอดขาย"}
                                  </button>
                                ) : (
                                  <button className="btn btn-sm btn-outline-secondary" disabled title="ใส่เรทแล้ว ลบจากหน้านี้ไม่ได้">
                                    ลบไม่ได้
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={9} className="bg-light">
                                <div className="p-3">
                                  <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                                    <div className="fw-semibold">รายการที่ขายในบิล #{order.bill_no}</div>
                                    <div className="small text-muted">
                                      รวม {sums.qty} ชิ้น / {formatCurrency(sums.usd, "USD")} / {formatCurrency(sums.thb)}
                                    </div>
                                  </div>
                                  <div className="table-responsive">
                                    <table className="table table-sm align-middle mb-0">
                                      <thead>
                                        <tr>
                                          <th>สินค้า</th>
                                          <th className="text-end">Qty</th>
                                          <th className="text-end">USD</th>
                                          <th className="text-end">Rate</th>
                                          <th className="text-end">THB</th>
                                          <th>Sold At</th>
                                          <th>Note</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {order.sale_records.map((rec) => {
                                          const item = itemById.get(rec.order_item_id);
                                          return (
                                            <tr key={rec.id}>
                                              <td>
                                                <div className="fw-semibold">{item?.product_code ?? `item #${rec.order_item_id}`}</div>
                                                <div className="small text-muted">{item?.product_name ?? "ไม่พบชื่อสินค้า"}</div>
                                              </td>
                                              <td className="text-end">{rec.qty}</td>
                                              <td className="text-end">{formatCurrency(rec.usd, "USD")}</td>
                                              <td className={`text-end ${rec.exchange_rate > 0 ? "" : "text-danger fw-semibold"}`}>
                                                {rec.exchange_rate > 0 ? rec.exchange_rate.toFixed(4) : "ยังไม่มี"}
                                              </td>
                                              <td className="text-end">{formatCurrency(rec.thb)}</td>
                                              <td>{formatDateTime(rec.sold_at)}</td>
                                              <td className="small text-muted">{rec.note ?? "-"}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
